import { useSyncExternalStore } from 'react';
import {
  blockDefaults,
  type Block,
  type BlockType,
  type RenderAsset,
  type Site,
  type SiteMeta,
  type SiteTheme,
  type ThemeFonts,
  type ThemePalette,
  type ThemeRadius,
  type ThemeSpacing,
  type ValidationError
} from '@sarnia/shared';
import { AutosaveController, type AutosaveState } from './autosave';
import { popFuture, popPast } from './undo';
import { getCsrfToken } from '../lib/api';

export type SaveState = 'idle' | 'dirty' | 'saving' | 'saved' | 'offline' | 'error';
export type Device = 'desktop' | 'tablet' | 'phone';
export type EditorPanel = 'block' | 'theme' | 'media' | 'publish';

export interface PendingImageTarget {
  blockId: string;
  propKey: string;
  altKey: string;
}

export interface EditorState {
  siteId: string;
  loadState: 'loading' | 'ready' | 'error';
  saveState: SaveState;
  lastSavedAt: string | null;
  site: Site | null;
  serverVersion: number;
  selectedBlockId: string | null;
  selectedPanel: EditorPanel;
  previewDevice: Device;
  assets: Record<string, RenderAsset>;
  clipboard: Block | null;
  past: Site[];
  future: Site[];
  validationErrors: ValidationError[];
  publishUrl: string | null;
  isPublishing: boolean;
  conflictVersion: number | null;
  pendingImageTarget: PendingImageTarget | null;
  saveError: string | null;
}

export class ApiError extends Error {
  status: number;
  code: string;
  details?: unknown;

  constructor(status: number, code: string, message: string, details?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

function getInitialEditorState(siteId = ''): EditorState {
  return {
    siteId,
    loadState: 'loading',
    saveState: 'idle',
    lastSavedAt: null,
    site: null,
    serverVersion: 0,
    selectedBlockId: null,
    selectedPanel: 'block',
    previewDevice: 'desktop',
    assets: {},
    clipboard: null,
    past: [],
    future: [],
    validationErrors: [],
    publishUrl: null,
    isPublishing: false,
    conflictVersion: null,
    pendingImageTarget: null,
    saveError: null
  };
}

async function parseApiError(response: Response): Promise<ApiError> {
  let payload: {
    error?: {
      code?: string;
      message?: string;
      details?: unknown;
    };
  } = {};
  try {
    payload = (await response.json()) as typeof payload;
  } catch {
    // No response body.
  }

  return new ApiError(
    response.status,
    payload.error?.code ?? 'BAD_REQUEST',
    payload.error?.message ?? 'Request failed',
    payload.error?.details
  );
}

export async function apiRequest<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers ?? {});
  headers.set('Accept', 'application/json');

  const bodyIsFormData = init.body instanceof FormData;
  const bodyIsString = typeof init.body === 'string';
  if (init.body && !bodyIsFormData) {
    if (!headers.has('Content-Type')) headers.set('Content-Type', 'application/json');
    if (!bodyIsString) init = { ...init, body: JSON.stringify(init.body) };
  }

  const csrfToken = getCsrfToken();
  if (csrfToken) {
    headers.set('x-csrf-token', csrfToken);
  }

  const response = await fetch(path, {
    credentials: 'same-origin',
    ...init,
    headers
  });

  if (!response.ok) {
    throw await parseApiError(response);
  }

  if (response.status === 204) {
    return undefined as T;
  }

  return (await response.json()) as T;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function readString(record: Record<string, unknown>, key: string): string | null {
  const value = record[key];
  return typeof value === 'string' ? value : null;
}

function normalizeMeta(meta: Record<string, unknown>): SiteMeta {
  return {
    title: readString(meta, 'title') ?? 'Untitled',
    description: readString(meta, 'description') ?? '',
    language: readString(meta, 'language') ?? 'en'
  };
}

function normalizeTheme(theme: Record<string, unknown>): SiteTheme | null {
  const palette = theme.palette;
  const fonts = theme.fonts;
  const radius = theme.radius;
  const spacing = theme.spacing;

  if (!isRecord(palette) || !isRecord(fonts) || !isRecord(radius) || !isRecord(spacing)) {
    return null;
  }

  const paletteValue: ThemePalette = {
    background: readString(palette, 'background') ?? '#FFFFFF',
    surface: readString(palette, 'surface') ?? '#F4F4F2',
    text: readString(palette, 'text') ?? '#111111',
    heading: readString(palette, 'heading') ?? '#111111',
    accent: readString(palette, 'accent') ?? '#13295E',
    accentText: readString(palette, 'accentText') ?? '#FFFFFF',
    muted: readString(palette, 'muted') ?? '#555555',
    border: readString(palette, 'border') ?? '#E5E5E2'
  };

  const fontsValue: ThemeFonts = {
    heading: readString(fonts, 'heading') ?? 'Archivo, sans-serif',
    body: readString(fonts, 'body') ?? 'Archivo, sans-serif',
    mono: readString(fonts, 'mono') ?? 'IBM Plex Mono, monospace'
  };

  const radiusValue: ThemeRadius = {
    sm: readString(radius, 'sm') ?? '0.25rem',
    md: readString(radius, 'md') ?? '0.5rem',
    lg: readString(radius, 'lg') ?? '1rem'
  };

  const spacingValue: ThemeSpacing = {
    section: readString(spacing, 'section') ?? '5.5rem',
    container: readString(spacing, 'container') ?? '76rem'
  };

  return {
    name: readString(theme, 'name') ?? 'Default',
    palette: paletteValue,
    fonts: fontsValue,
    radius: radiusValue,
    spacing: spacingValue
  };
}

function deepMerge<T>(base: T, patch: unknown): T {
  // Arrays are values, not records: replace them wholesale (spreading one into an object would give {"0": ...}).
  if (Array.isArray(patch) || Array.isArray(base) || !isRecord(base) || !isRecord(patch)) {
    return (patch as T) ?? base;
  }

  const output: Record<string, unknown> = { ...base };

  for (const [key, value] of Object.entries(patch)) {
    if (isRecord(value) && isRecord(output[key])) {
      output[key] = deepMerge(output[key], value);
    } else {
      output[key] = value;
    }
  }

  return output as T;
}

function cloneBlock(block: Block): Block {
  return JSON.parse(JSON.stringify(block)) as Block;
}

function cloneBlockWithNewId(block: Block): Block {
  const clone = cloneBlock(block);
  clone.id = crypto.randomUUID();

  const children = (block as Block & { children?: Block[] }).children;
  if (Array.isArray(children)) {
    (clone as Block & { children: Block[] }).children = children.map(cloneBlockWithNewId);
  }

  return clone;
}

function normalizeSite(input: unknown): Site | null {
  if (!isRecord(input)) {
    return null;
  }

  const blocks = Array.isArray(input.blocks) ? input.blocks : safeParseArray(input.blocks_json);
  const rawTheme = isRecord(input.theme) ? input.theme : safeParseObject(input.theme_json);
  const rawMeta = isRecord(input.meta)
    ? input.meta
    : {
        title: typeof input.title === 'string' ? input.title : 'Untitled',
        description: typeof input.description === 'string' ? input.description : '',
        language: typeof input.language === 'string' ? input.language : 'en'
      };

  if (!Array.isArray(blocks) || !isRecord(rawTheme) || !isRecord(rawMeta)) {
    return null;
  }

  const theme = normalizeTheme(rawTheme);
  if (!theme) {
    return null;
  }

  return {
    version: 1,
    meta: normalizeMeta(rawMeta),
    theme,
    blocks: blocks as Block[]
  };
}

function safeParseObject(input: unknown): Record<string, unknown> | null {
  if (isRecord(input)) {
    return input;
  }
  if (typeof input === 'string') {
    try {
      const parsed = JSON.parse(input) as unknown;
      return isRecord(parsed) ? parsed : null;
    } catch {
      return null;
    }
  }
  return null;
}

function safeParseArray(input: unknown): unknown[] | null {
  if (Array.isArray(input)) {
    return input;
  }
  if (typeof input === 'string') {
    try {
      const parsed = JSON.parse(input) as unknown;
      return Array.isArray(parsed) ? parsed : null;
    } catch {
      return null;
    }
  }
  return null;
}

function normalizeAssets(input: unknown): Record<string, RenderAsset> {
  if (!Array.isArray(input)) {
    return {};
  }

  const assets: Record<string, RenderAsset> = {};

  for (const value of input) {
    if (!isRecord(value)) {
      continue;
    }

    const assetId = typeof value.id === 'string'
      ? value.id
      : typeof value.assetId === 'string'
        ? value.assetId
        : null;
    const url = typeof value.url === 'string'
      ? value.url
      : typeof value.staticUrl === 'string'
        ? value.staticUrl
        : null;

    if (!assetId || !url) {
      continue;
    }

    assets[assetId] = {
      assetId,
      url,
      alt: typeof value.alt_text === 'string'
        ? value.alt_text
        : typeof value.alt === 'string'
          ? value.alt
          : '',
      width: typeof value.width === 'number' ? value.width : undefined,
      height: typeof value.height === 'number' ? value.height : undefined
    };
  }

  return assets;
}

function extractVersion(input: unknown): number {
  if (isRecord(input)) {
    const version = input.version;
    if (typeof version === 'number') {
      return version;
    }
  }
  return 0;
}

type Listener = () => void;

class EditorStore {
  private state: EditorState = getInitialEditorState();
  private listeners = new Set<Listener>();
  private autosaveController: AutosaveController | null = null;
  private loadRequestId = 0;

  private setState(patch: Partial<EditorState>): void {
    this.state = { ...this.state, ...patch };
    this.listeners.forEach((listener) => listener());
  }

  getState(): EditorState {
    return this.state;
  }

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  private initAutosave(): void {
    if (this.autosaveController) {
      return;
    }

    this.autosaveController = new AutosaveController({
      debounceMs: 1500,
      save: (site, version, siteId) => this.handleSave(site, version, siteId),
      onStateChange: (state: AutosaveState, details) => {
        if (state === 'saving') {
          this.setState({ saveState: 'saving', saveError: null });
        } else if (state === 'saved') {
          this.setState({
            saveState: 'saved',
            lastSavedAt: new Date().toISOString(),
            conflictVersion: null,
            saveError: null
          });
        } else if (state === 'offline') {
          this.setState({ saveState: 'offline', saveError: 'You are offline. Changes will be retried.' });
        } else if (state === 'error') {
          this.setState({ saveState: 'error', saveError: details?.message ?? 'Could not save changes.' });
        }
      }
    });
  }

  private commit(nextSite: Site): void {
    const current = this.state.site;
    const serverVersion = this.state.serverVersion;
    const past = current ? [...this.state.past, current].slice(-100) : this.state.past;

    this.setState({
      site: nextSite,
      past,
      future: [],
      validationErrors: [],
      saveState: 'dirty',
      saveError: null
    });

    this.autosaveController?.schedule(nextSite, serverVersion, this.state.siteId);
  }

  private async handleSave(site: Site, _scheduledVersion: number, siteId: string): Promise<void> {
    try {
      // Always send the newest server version: a queued save was scheduled before the previous save returned.
      const payload = {
        version: this.state.serverVersion,
        title: site.meta.title,
        description: site.meta.description,
        language: site.meta.language,
        theme: site.theme,
        blocks: site.blocks
      };

      const response = await apiRequest<{ site: unknown }>(`/api/v1/sites/${siteId}`, {
        method: 'PATCH',
        body: JSON.stringify(payload)
      });

      const nextVersion = extractVersion(response.site);

      // Keep the local site as is: the user may have kept typing while this save was in flight.
      this.setState({
        serverVersion: nextVersion,
        conflictVersion: null,
        saveError: null
      });
    } catch (error) {
      if (error instanceof ApiError && error.status === 409 && error.code === 'VERSION_CONFLICT') {
        const conflicts = isRecord(error.details)
          ? (error.details as { serverVersion?: unknown }).serverVersion
          : null;
        this.setState({
          saveState: 'error',
          conflictVersion: typeof conflicts === 'number' ? conflicts : null,
          saveError: 'Another editor saved this site. You can keep your version or reload the latest.'
        });
      } else if (error instanceof ApiError) {
        this.setState({ saveState: 'error', saveError: error.message });
      } else if (error instanceof TypeError || (typeof navigator !== 'undefined' && !navigator.onLine)) {
        this.setState({ saveState: 'offline', saveError: 'You are offline. Changes will be retried.' });
      } else {
        this.setState({ saveState: 'error', saveError: 'Could not save changes.' });
      }

      throw error;
    }
  }

  async loadSite(siteId: string): Promise<void> {
    const requestId = ++this.loadRequestId;
    this.autosaveController?.cancel();
    this.setState({
      ...getInitialEditorState(siteId),
      siteId,
      loadState: 'loading'
    });

    try {
      const [siteResponse, assetsResponse] = await Promise.all([
        apiRequest<{ site: unknown; publishedUrl?: string | null }>(`/api/v1/sites/${siteId}`),
        apiRequest<{ assets: unknown }>(`/api/v1/sites/${siteId}/assets`)
      ]);

      if (requestId !== this.loadRequestId) {
        return;
      }

      const site = normalizeSite(siteResponse.site);
      if (!site) {
        throw new Error('The server returned an invalid site.');
      }

      this.setState({
        site,
        assets: normalizeAssets(assetsResponse.assets),
        serverVersion: extractVersion(siteResponse.site),
        publishUrl: siteResponse.publishedUrl ?? null,
        loadState: 'ready',
        saveState: 'idle',
        saveError: null,
        past: [],
        future: []
      });

      this.initAutosave();
    } catch (error) {
      if (requestId !== this.loadRequestId) {
        return;
      }

      this.setState({
        loadState: 'error',
        saveState: 'error',
        saveError: error instanceof Error ? error.message : 'Could not load this site.'
      });
    }
  }

  cancelForTest(): void {
    this.loadRequestId += 1;
    this.autosaveController?.cancel();
    this.autosaveController = null;
    this.setState(getInitialEditorState());
  }

  setTestSite(site: Site): void {
    this.loadRequestId += 1;
    this.autosaveController?.cancel();
    this.autosaveController = null;
    this.setState({
      ...getInitialEditorState(),
      siteId: 'site-1',
      site,
      loadState: 'ready',
      saveState: 'idle'
    });
  }

  selectBlock(id: string | null): void {
    this.setState({ selectedBlockId: id });
  }

  selectPanel(panel: EditorPanel): void {
    this.setState({ selectedPanel: panel, pendingImageTarget: null });
  }

  setDevice(device: Device): void {
    this.setState({ previewDevice: device });
  }

  updateBlockProps(id: string, patch: Record<string, unknown>): void {
    const site = this.state.site;
    if (!site) {
      return;
    }

    const blocks = site.blocks.map((block) => {
      if (block.id !== id) {
        return block;
      }

      // Designed blocks replace whole top-level keys (so removing an image, link or colour override really removes it).
      const updatedProps = block.type === 'designed' ? { ...(block.props as object), ...patch } : deepMerge(block.props, patch);
      return { ...block, props: updatedProps } as Block;
    });

    this.commit({ ...site, blocks });
  }

  /** Sets a section's background / spacing. Passing 'none' or 'normal' clears that part. */
  updateBlockStyle(id: string, patch: { background?: 'none' | 'surface' | 'tint' | 'accent'; space?: 'compact' | 'normal' | 'roomy' }): void {
    const site = this.state.site;
    if (!site) {
      return;
    }

    const blocks = site.blocks.map((block) => {
      if (block.id !== id) {
        return block;
      }
      const next = { ...(block.style ?? {}), ...patch };
      if (next.background === 'none') delete next.background;
      if (next.space === 'normal') delete next.space;
      const { style: _old, ...rest } = block as Block & { style?: unknown };
      void _old;
      return (Object.keys(next).length > 0 ? { ...rest, style: next } : rest) as Block;
    });

    this.commit({ ...site, blocks });
  }

  addBlock(type: BlockType, afterId?: string): void {
    const site = this.state.site;
    const fallback = blockDefaults[type];
    if (!site || !fallback) {
      return;
    }

    const block = {
      id: crypto.randomUUID(),
      type,
      props: JSON.parse(JSON.stringify(fallback)) as Block['props']
    } as Block;

    const blocks = [...site.blocks];
    // Where it goes: a header always leads, a footer always closes; anything else follows the block you asked for (or the one
    // currently selected) and never lands underneath the footer.
    const refId = afterId ?? this.state.selectedBlockId ?? undefined;
    const refIndex = refId ? blocks.findIndex((item) => item.id === refId) : -1;
    let insertAt: number;
    if (type === 'header') {
      insertAt = 0;
    } else if (type === 'footer') {
      insertAt = blocks.length;
    } else if (refIndex >= 0) {
      insertAt = blocks[refIndex]!.type === 'footer' ? refIndex : refIndex + 1;
    } else {
      insertAt = blocks.length > 0 && blocks[blocks.length - 1]!.type === 'footer' ? blocks.length - 1 : blocks.length;
    }
    blocks.splice(insertAt, 0, block);

    this.commit({ ...site, blocks });
    this.setState({ selectedBlockId: block.id });
  }

  moveBlock(id: string, afterId?: string): void {
    const site = this.state.site;
    if (!site) {
      return;
    }

    const blocks = [...site.blocks];
    const fromIndex = blocks.findIndex((block) => block.id === id);
    if (fromIndex < 0) {
      return;
    }

    const [moved] = blocks.splice(fromIndex, 1);
    if (!moved) {
      return;
    }

    let toIndex = blocks.length;

    if (afterId !== undefined && afterId !== id) {
      const afterIndex = blocks.findIndex((block) => block.id === afterId);
      if (afterIndex >= 0) {
        toIndex = afterIndex + 1;
      }
    }

    blocks.splice(toIndex, 0, moved);
    this.commit({ ...site, blocks });
  }

  duplicateBlock(id: string): void {
    const site = this.state.site;
    if (!site) {
      return;
    }

    const index = site.blocks.findIndex((block) => block.id === id);
    if (index < 0) {
      return;
    }

    const blockToCopy = site.blocks[index];
    if (!blockToCopy) {
      return;
    }

    const duplicate = cloneBlockWithNewId(blockToCopy);
    const blocks = [...site.blocks];
    blocks.splice(index + 1, 0, duplicate);

    this.commit({ ...site, blocks });
    this.setState({ selectedBlockId: duplicate.id });
  }

  removeBlock(id: string): void {
    const site = this.state.site;
    if (!site) {
      return;
    }

    const blocks = site.blocks.filter((block) => block.id !== id);
    this.commit({ ...site, blocks });

    if (this.state.selectedBlockId === id) {
      this.setState({ selectedBlockId: null });
    }
  }

  updateTheme(patch: Partial<SiteTheme>): void {
    const site = this.state.site;
    if (!site) {
      return;
    }

    this.commit({ ...site, theme: deepMerge(site.theme, patch) });
  }

  updateMeta(patch: Partial<SiteMeta>): void {
    const site = this.state.site;
    if (!site) {
      return;
    }

    this.commit({ ...site, meta: { ...site.meta, ...patch } });
  }

  setBlocks(blocks: Block[]): void {
    const site = this.state.site;
    if (!site) {
      return;
    }

    this.commit({ ...site, blocks });
  }

  undo(): void {
    const { site, past, future, serverVersion, siteId } = this.state;
    if (!site) {
      return;
    }

    const result = popPast(past, site, future);
    if (!result) {
      return;
    }

    this.setState({
      site: result.site,
      past: result.past,
      future: result.future,
      saveState: 'dirty',
      saveError: null,
      validationErrors: []
    });

    this.autosaveController?.schedule(result.site, serverVersion, siteId);
  }

  redo(): void {
    const { site, past, future, serverVersion, siteId } = this.state;
    if (!site) {
      return;
    }

    const result = popFuture(past, site, future);
    if (!result) {
      return;
    }

    this.setState({
      site: result.site,
      past: result.past,
      future: result.future,
      saveState: 'dirty',
      saveError: null,
      validationErrors: []
    });

    this.autosaveController?.schedule(result.site, serverVersion, siteId);
  }

  async autosaveNow(): Promise<void> {
    await this.autosaveController?.flush();
  }

  /**
   * Waits until every change has reached the server, so version history works on what the owner sees. Throws if the
   * latest changes could not be saved, rather than letting a restore or a named save quietly leave them out.
   */
  async ensureSaved(timeoutMs = 15000): Promise<void> {
    const controller = this.autosaveController;
    if (controller) {
      const started = Date.now();
      do {
        await controller.flush();
        if (controller.hasPendingSave()) await new Promise((resolve) => setTimeout(resolve, 100));
      } while (controller.hasPendingSave() && Date.now() - started < timeoutMs);
    }
    if (this.state.saveState === 'error' || this.state.saveState === 'offline' || this.state.saveState === 'dirty') {
      throw new Error('Your latest changes have not been saved yet. Try again in a moment.');
    }
  }

  /** Replaces the page with an earlier version. The server keeps the current content in the history first. */
  async restoreVersion(versionId: string): Promise<void> {
    const { siteId } = this.state;
    await this.ensureSaved();
    await apiRequest(`/api/v1/sites/${siteId}/versions/${encodeURIComponent(versionId)}/restore`, { method: 'POST' });
    await this.loadSite(siteId);
  }

  async publish(): Promise<void> {
    const { site, siteId } = this.state;
    if (!site) {
      return;
    }

    this.setState({ isPublishing: true, validationErrors: [] });

    try {
      const result = await apiRequest<{ versionId: string; versionNo: number; url: string }>(
        `/api/v1/sites/${siteId}/publish`,
        { method: 'POST', body: '{}' }
      );

      this.setState({
        isPublishing: false,
        publishUrl: result.url,
        saveError: null
      });
    } catch (error) {
      if (error instanceof ApiError && error.status === 422 && error.code === 'PUBLISH_VALIDATION_FAILED') {
        const details = isRecord(error.details)
          ? (error.details as { errors?: unknown }).errors
          : null;
        const errors = Array.isArray(details) ? (details as ValidationError[]) : [];
        this.setState({
          isPublishing: false,
          validationErrors: errors,
          saveState: 'error',
          saveError: 'Some content needs attention before this site can go live.'
        });
      } else {
        this.setState({
          isPublishing: false,
          saveState: 'error',
          saveError: error instanceof Error ? error.message : 'Could not publish the site.'
        });
      }
    }
  }

  async unpublish(): Promise<void> {
    const { siteId } = this.state;
    try {
      await apiRequest(`/api/v1/sites/${siteId}/unpublish`, { method: 'POST', body: '{}' });
      this.setState({ publishUrl: null, saveError: null });
    } catch (error) {
      this.setState({
        saveState: 'error',
        saveError: error instanceof Error ? error.message : 'Could not unpublish the site.'
      });
    }
  }

  async uploadAsset(file: File, alt = ''): Promise<RenderAsset | null> {
    const { siteId } = this.state;
    const formData = new FormData();
    formData.append('file', file);
    formData.append('alt', alt);

    try {
      const response = await apiRequest<{ asset: unknown }>(`/api/v1/sites/${siteId}/assets`, {
        method: 'POST',
        body: formData
      });

      const normalized = normalizeAssets([response.asset]);
      const asset = Object.values(normalized)[0] ?? null;
      if (asset) {
        this.setState({ assets: { ...this.state.assets, [asset.assetId]: asset } });
      }
      return asset;
    } catch (error) {
      this.setState({
        saveState: 'error',
        saveError: error instanceof Error ? error.message : 'Could not upload this image.'
      });
      return null;
    }
  }

  async updateAssetAlt(assetId: string, alt: string): Promise<void> {
    const asset = this.state.assets[assetId];
    if (!asset) {
      return;
    }

    this.setState({
      assets: { ...this.state.assets, [assetId]: { ...asset, alt } }
    });

    try {
      await apiRequest(`/api/v1/assets/${assetId}`, {
        method: 'PATCH',
        body: JSON.stringify({ alt })
      });
    } catch (error) {
      this.setState({
        saveState: 'error',
        saveError: error instanceof Error ? error.message : 'Could not update image alt text.'
      });
    }
  }

  reconcileRemote(_remoteSite: Site, serverVersion: number): void {
    this.setState({
      serverVersion,
      conflictVersion: null,
      saveState: 'saved',
      saveError: null,
      lastSavedAt: new Date().toISOString()
    });
    this.autosaveController?.cancel();
  }

  startImagePick(blockId: string, propKey: string, altKey: string): void {
    this.setState({
      pendingImageTarget: { blockId, propKey, altKey },
      selectedPanel: 'media'
    });
  }

  applyAsset(asset: RenderAsset): void {
    const target = this.state.pendingImageTarget;
    if (!target) {
      return;
    }

    this.updateBlockProps(target.blockId, {
      [target.propKey]: { assetId: asset.assetId }
    });

    this.setState({
      pendingImageTarget: null,
      selectedPanel: 'block'
    });
  }
}

export const editorStore = new EditorStore();

export function useEditorStore(): EditorState {
  return useSyncExternalStore(
    (listener) => editorStore.subscribe(listener),
    () => editorStore.getState()
  );
}

export function useEditorSelector<T>(selector: (state: EditorState) => T): T {
  return useSyncExternalStore(
    (listener) => editorStore.subscribe(listener),
    () => selector(editorStore.getState())
  );
}
